// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Updated: 2026-04-24 — getPublicApexHref: siempre ?lang= (paridad marketing + page.php).
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Constantes globales de la aplicación ARCHAT
 */

import {
  SUPPORTED_LANGUAGES as SUPPORTED_LANGUAGE_CODES,
  SUPPORTED_LANGUAGE_SET,
  SUPPORTED_LANGUAGE_DEFINITIONS,
} from '../i18n/supportedLanguages.js';

// Versión del build
export const BUILD_VERSION = '2.1.12';

/** Prefijo SPA canónico (debe coincidir con `BrowserRouter basename`). Fuente única; no concatenar `/app` manualmente encima. */
export const BASE_PATH = '/app';

/**
 * Ruta canónica interna (pathname relativo al basename) para el modal de planes / futuro Paddle.
 * URL absoluta típica: `{origin}/app/upgrade?source=...&plan=...`
 */
export const SPA_UPGRADE_PATH = '/upgrade';

/** Query params reconocidos en SPA_UPGRADE_PATH (atribución / preselección). */
export const SPA_UPGRADE_QUERY = {
  source: 'source',
  plan: 'plan',
};

/** Sitio comercial / marketing (fuera del SPA `/app/`). Logo navbar abre esta URL. */
export const MARKETING_SITE_URL = 'https://www.archat.com.ar/';

/**
 * Rutas legales HTML públicas en el apex del dominio (sin `/app/`), servidas por `api/legal/page.php`.
 * Parámetro `?lang=xx` siempre presente (incl. `es`) — paridad con `page.php` y `sitio_web/js/site-i18n.js`.
 * URL típica: `https://www.archat.com.ar` + path + `?lang=en` (o el código de interfaz).
 */
export const LEGAL_PUBLIC_PATHS = {
  termsAndPolicies: '/terms-and-policies',
  termsOfService: '/terms-of-service',
  privacyPolicy: '/privacy-policy',
  refundPolicy: '/refund-policy',
  accountDeletion: '/account-deletion',
};

/** Ruta canónica en apex (misma familia que legales /terms-*). Servida por `backend/api/public/pricing.php`. */
export const PUBLIC_PRICING_APEX_PATH = '/pricing';

/**
 * Normaliza el idioma de la SPA al roster oficial (BCP-47: `en-US` → `en`). Desconocido → `es`.
 * Paridad de intención con legales públicas (`page.php` / marketing).
 * @param {string|undefined} lang
 * @returns {string}
 */
function publicApexLangCode(lang) {
  if (typeof lang !== 'string' || !lang.trim()) {
    return 'es';
  }
  const base = lang.split(/[-_]/)[0].toLowerCase();
  return SUPPORTED_LANGUAGE_SET.has(base) ? base : 'es';
}

/**
 * Enlace a la página pública de precios (apex). La ruta SPA `/app/pricing` solo redirige aquí.
 * URL absoluta canónica (`www.archat.com.ar`) para enlaces desde el SPA.
 * @param {string} [lang] - Código idioma; siempre se añade `?lang=` (incl. `es`).
 * @returns {string}
 */
export function getPublicPricingHref(lang) {
  const base = MARKETING_SITE_URL.replace(/\/$/, '');
  const path = `${base}/pricing`;
  const code = publicApexLangCode(lang);
  return `${path}?lang=${encodeURIComponent(code)}`;
}

/**
 * Enlace absoluto a una ruta pública en el apex (legales, home marketing), siempre con `?lang=`.
 * @param {string} path - Ruta absoluta en el sitio, p. ej. `/terms-of-service` o `LEGAL_PUBLIC_PATHS.termsOfService`.
 * @param {string} [lang] - Código de idioma activo (i18n SPA / HomePublic).
 * @returns {string}
 */
export function getPublicApexHref(path, lang) {
  const base = MARKETING_SITE_URL.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  const full = `${base}${p}`;
  const code = publicApexLangCode(lang);
  return `${full}?lang=${encodeURIComponent(code)}`;
}

/**
 * Rutas de auth/legal en la URL real del navegador (incluye BASE_PATH).
 * Usar con `window.location.pathname`; no mezclar con rutas internas del Router (sin prefijo BASE_PATH).
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function isFullSpaAuthPath(pathname) {
  if (typeof pathname !== 'string' || pathname === '') return false;
  const root = BASE_PATH.replace(/\/$/, '');
  if (!root || root === '/') {
    return (
      pathname.startsWith('/login')
      || pathname.startsWith('/register')
      || pathname.startsWith('/reset-password')
      || pathname.startsWith('/legal')
    );
  }
  return (
    pathname.startsWith(`${root}/login`)
    || pathname.startsWith(`${root}/register`)
    || pathname.startsWith(`${root}/reset-password`)
    || pathname.startsWith(`${root}/legal`)
  );
}

// Base URL de la API (backend en /api; independiente del prefijo SPA /app)
// 🔹 Inyectado por VITE_API_BASE en build; fallback /api.
// Acceso seguro compatible con Node (Playwright/Jest): evita error import.meta.env
const viteEnv = (typeof import.meta !== 'undefined' && import.meta?.env) ? import.meta.env : {};
export const API_BASE = viteEnv.VITE_API_BASE || '/api';

/**
 * URL absoluta para fetch/XHR cuando el path ya incluye /api/ o hay que anteponer API_BASE.
 * Evita /api/api/ al concatenar con API_BASE.
 * @param {string} path - ej. /api/system/diag_identity.php o /perfil.php
 * @returns {string}
 */
export function joinApiOrigin(path) {
  if (typeof path !== 'string' || !path.trim()) return '';
  const p = path.startsWith('/') ? path : `/${path}`;
  if (typeof window === 'undefined') {
    if (API_BASE && API_BASE.startsWith('http')) {
      const base = API_BASE.replace(/\/$/, '');
      const rel = p.startsWith('/api/') ? p.replace(/^\/api/, '') || '/' : p;
      return base + (rel.startsWith('/') ? rel : `/${rel}`);
    }
    return p;
  }
  if (API_BASE && API_BASE.startsWith('http')) {
    const base = API_BASE.replace(/\/$/, '');
    const rel = p.startsWith('/api/') ? p.replace(/^\/api/, '') || '/' : p;
    return base + (rel.startsWith('/') ? rel : `/${rel}`);
  }
  if (p.startsWith('/api/')) {
    return window.location.origin + p;
  }
  const b = typeof API_BASE === 'string' && API_BASE ? API_BASE.replace(/\/$/, '') : '/api';
  return window.location.origin + b + (p.startsWith('/') ? p : `/${p}`);
}

// Base URL de WebSocket
// 🔹 En desarrollo: /socket.io
// 🔹 En producción: se inyecta por VITE_WS_BASE
export const WS_BASE = viteEnv.VITE_WS_BASE || '/socket.io';

// Ruta base de la aplicación (VITE_PUBLIC_BASE opcional; por defecto BASE_PATH)
const buildBase = (() => {
  const raw = viteEnv.VITE_PUBLIC_BASE;
  if (typeof raw === 'string' && raw.trim() !== '') {
    return raw.replace(/\/$/, '') || '/';
  }
  return BASE_PATH;
})();

export const getBasePath = () => {
  if (buildBase === '/' || buildBase === '') return '/';

  if (typeof window === 'undefined') return buildBase;

  const hostname = window.location.hostname;
  const pathname = window.location.pathname;

  // Desarrollo local
  if (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.')
  ) {
    return buildBase;
  }

  // Dominio productivo
  if (hostname === 'www.archat.com.ar' || hostname === 'archat.com.ar') {
    return buildBase;
  }

  if (pathname.startsWith(buildBase)) return buildBase;

  return buildBase;
};

if (typeof console !== 'undefined' && typeof window !== 'undefined') {
  console.warn('[BASE_PATH_DEBUG]', { value: BASE_PATH, resolved: getBasePath() });
}

// Idiomas soportados - derivados de la fuente única de verdad
export const SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGE_CODES.map((code) => ({
  code,
  ...SUPPORTED_LANGUAGE_DEFINITIONS[code],
}));

/** Raíz del SPA dentro de `basename` (/app → URL /app/). No usar '/app' aquí o se duplica la ruta. */
export const AUTH_SUCCESS_REDIRECT = '/';

// Estados de presencia
export const PRESENCE_STATUSES = {
  ONLINE: 'online',
  IDLE: 'idle',
  DND: 'dnd',
  BUSY: 'busy',
  OFFLINE: 'offline',
  CUSTOM: 'custom'
};

// Intervalos de polling
export const MESSAGE_POLLING_INTERVAL = 5000;
export const CONTACTS_POLLING_INTERVAL = 5000;

// Tipos de archivos permitidos
export const ALLOWED_FILE_TYPES = {
  IMAGE: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  VIDEO: ['video/mp4', 'video/webm', 'video/ogg'],
  AUDIO: ['audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/webm'],
  PDF: ['application/pdf']
};

// Tamaño máximo de archivo
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Endpoints API: siempre bajo /api/ (mismo host en dev vía proxy Vite)
export const API_ENDPOINTS = {
  LOGIN: '/api/auth/login.php',
  GOOGLE_LOGIN: '/api/auth/google_login.php',
  ACCEPT_TERMS: '/api/auth/accept_terms.php',
  DIAG_IDENTITY: '/api/system/diag_identity.php',
  /** Reporte automático de errores (observabilidad). POST JSON. */
  REPORT_ERROR_AUTO: '/api/system/report_error_auto.php',
  /** Métricas Founder (cupos restantes, total). GET. */
  FOUNDER_METRICS: '/api/system/founder_metrics.php',
  /** Top Founders leaderboard. GET. */
  FOUNDER_LEADERBOARD: '/api/system/founder_leaderboard.php',
  AUTH_CHECK: '/api/auth/check.php',
  CHECK_USERNAME: '/api/auth/check-username.php',
  CHECK_EMAIL: '/api/auth/check-email.php',
  REGISTER: '/api/auth/registrar.php',
  GENERATE_CAPTCHA: '/api/auth/generar_captcha.php',
  PROFILE: '/api/perfil.php',
  PROFILE_META: '/api/profile/meta.php',
  MESSAGES: '/api/mensajes.php',
  DASHBOARD_RECENT_INBOUND: '/api/dashboard_recent_inbound.php',
  CONVERSATIONS: '/api/conversaciones.php',
  CREATE_CONVERSATION: '/api/crear_conversacion.php',
  CONTACTS: '/api/listar_usuarios.php',
  CONTACTS_MANAGE: '/api/contactos.php',
  PRESENCE: '/api/presence/status.php',
  /** Catálogo preset_key (PresencePresetRegistry); orden y claves i18n desde backend */
  PRESENCE_PRESETS: '/api/presence/presets.php',
  PRESENCE_CUSTOM: '/api/presence/custom.php',
  TRANSLATION: '/api/translation_service.php',
  TRANSLATE: '/api/translate.php',
  UPLOAD_AVATAR: '/api/upload_avatar.php',
  COUNTRIES: '/api/paises.php',
  RESET_PASSWORD: '/api/auth/solicitar_reset.php',
  CONFIRM_RESET_PASSWORD: '/api/auth/confirmar_reset.php',
  REQUEST_DELETE_ACCOUNT: '/api/auth/solicitar_eliminacion.php',
  CONFIRM_DELETE_ACCOUNT: '/api/auth/confirmar_eliminacion.php',
  RESEND_CONFIRMATION: '/api/auth/resend-confirmation.php',
  RESEND_ACTIVATION: '/api/auth/resend_activation.php',
  LEGAL_TERMS: '/api/legal/terms.php',
  LEGAL_PRIVACY: '/api/legal/privacy.php',
  LEGAL_SUBSCRIPTION: '/api/legal/subscription.php',
  LEGAL_REFUNDS: '/api/legal/refunds.php',
  LEGAL_COOKIES: '/api/legal/cookies.php',
  VERIFY_EMAIL: '/api/auth/verificar_email.php',
  ACTIVATE_ACCOUNT: '/api/auth/activate_account.php',
  COMPLETE_ACTIVATION: '/api/auth/complete_activation.php',
  LOGS: '/api/logs.php',
  TYPING_STATUS: '/api/typing_status.php',
  BLOCKS: '/api/bloqueos.php',
  FAVORITES: '/api/favoritos.php',
  UPDATE_CONTACT: '/api/contactos.php',
  SEND_INVITATION: '/api/enviar_invitacion.php',
  VALIDATE_INVITATION: '/api/validar_invitacion.php',
  ACCEPT_INVITATION: '/api/aceptar_invitacion.php',
  LIST_INVITATIONS: '/api/listar_invitaciones.php',
  CANCEL_INVITATION: '/api/cancelar_invitacion.php',
  RESEND_INVITATION: '/api/reenviar_invitacion.php',
  PLAN: '/api/plan.php',
  UPGRADE_PLAN: '/api/upgrade_plan.php',
  BILLING_CREATE_PREFERENCE: '/api/billing/create_preference.php',
  BILLING_CREATE_SUBSCRIPTION: '/api/billing/create-subscription.php',
  BILLING_SUBSCRIBE: '/api/billing/subscribe.php',
  BILLING_STATUS: '/api/billing/status.php',
  BILLING_SUBSCRIPTION_STATUS: '/api/billing/subscription_status.php',
  BILLING_CANCEL: '/api/billing/cancel.php',
  BILLING_CANCEL_SUBSCRIPTION: '/api/billing/cancel_subscription.php',
  BILLING_CHANGE_PLAN: '/api/billing/change_plan.php',
  BILLING_PADDLE_PORTAL_SESSION: '/api/billing/paddle_portal_session.php',
  BILLING_SCHEDULED_PLAN_CHANGE: '/api/billing/scheduled_plan_change.php',
  BILLING_UPGRADE: '/api/billing/upgrade.php',
  TENANT_USAGE: '/api/tenant/usage.php',
  /** Plan efectivo (Motor de Planes). Usar solo con FEATURE_PLAN_UI_SOURCE_OF_TRUTH_V1. */
  PLAN_EFFECTIVE: '/api/plan/effective.php',
  /** Resumen unificado (Dashboard + Perfil). Fuente única: plan_code, plan_display, limits, usage. */
  PLAN_SUMMARY: '/api/plan/summary.php',
  PLAN_INFO: '/api/plan/info.php',
  ADMIN_TENANTS: '/api/admin/tenants.php',
  ADMIN_TENANT_DETAILS: '/api/admin/tenant_details.php',
  FRONTEND_ERROR_LOG: '/api/log.php',
  USER_STATUS_GET: '/api/presence/status.php',
  USER_STATUS_UPDATE: '/api/user/status_update.php',
  USER_ONBOARDING_COMPLETE: '/api/user/onboarding_complete.php',
  USER_THEME: '/api/user/theme.php',
  LEER: '/api/leer.php',
  FEATURE_FLAGS: '/api/feature_flags.php',
  /** Crear sugerencia de usuario (v2). POST JSON { message, title? }. */
  CREATE_SUGGESTION: '/api/create_suggestion.php',
  /** Listar sugerencias del usuario autenticado (historial completo). */
  MY_SUGGESTIONS: '/api/my_suggestions.php',
  /** Compat legacy de creación de sugerencias. */
  SUGGESTIONS_CREATE: '/api/suggestions/create.php',
  USER_UPDATE_PREFERENCES: '/api/user/update_preferences.php',
  FILES: '/api/files.php'
};

// Claves de sessionStorage/localStorage para flujos específicos
export const LOGIN_LANGUAGE_OVERRIDE_KEY = 'archat_login_language';
export const ONBOARDING_DONE_FALLBACK_KEY = 'archat_onboarding_done';

/** Invitaciones: cooldown y límite diario de reenvío */
export const RESEND_COOLDOWN_MS = 10 * 60 * 1000;
export const COOLDOWN_STORAGE_KEY = 'archat_invitation_cooldowns';
export const DAILY_RESEND_STORAGE_KEY = 'archat_invitation_daily_resend';
export const DAILY_RESEND_MAX = 3;

/** Estilo para cards del AdminMetricsDashboard */
export const ADMIN_METRICS_CARD_STYLE = {
  border: '1px solid #d0d7de',
  borderRadius: '10px',
  padding: '14px',
  minHeight: '96px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between'
};

// Claves de localStorage (tema: ThemeContext; solo argentina|dark)
export const STORAGE_KEYS = {
  USER_ID: 'archat_usuario_id',
  USER_DATA: 'archat_user',
  LANGUAGE: 'archat_language',
  TOKEN: 'archat_token',
  THEME: 'archat_theme',
  REGION: 'archat_region',
  BLOCKED: 'archat_blocked_users',
  FAVORITES: 'archat_favorite_users',
  /**
   * Base del orden manual de contactos. Persistencia efectiva por cuenta:
   * `${CONTACTS_MANUAL_PEER_ORDER}_${usuarioId}` (ver contactsManualOrderStorage.js).
   */
  CONTACTS_MANUAL_PEER_ORDER: 'archat_contacts_manual_peer_order_v1',
};

